"use client";
import { ZAP_PAGE_SIZE } from "@zapier/utils";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useRouterParams } from "../hooks/useRouterParams";

/***
 * PAGE SIZE CONTEXT
 * The purpose of this provider is to store a page size preference for all tables in
 * the AssetManagement frontend application. The value here is stored in localStorage
 * and applied to all asset list view tables.
 */

interface PageSize {
  size: number;
}

interface PageSizeAPI extends PageSize {
  setSize?: (size: PageSize) => void;
}

const defaultPageSize = {
  size: ZAP_PAGE_SIZE,
};

const PageSizeContext = createContext<PageSizeAPI | null>(null);

export const PageSizeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [pageSize, setPageSize] = useState<PageSize>(defaultPageSize);
  const { page, setParams } = useRouterParams();

  // When the component mounts, check if there is a page size preference in localStorage
  useEffect(() => {
    const ps = globalThis.localStorage.getItem("AM:pageSize");
    setPageSize({ size: ps ? Number.parseInt(ps) : ZAP_PAGE_SIZE });
  }, []);

  // When setting page size, update the state and localStorage
  const setSize = useCallback(
    (size: PageSize) => {
      if (size.size !== pageSize.size) {
        setPageSize(size);
        globalThis.localStorage.setItem("AM:pageSize", size.size.toString());
        // always set page to 1 when changing page size
        if (page && page > 1) {
          setParams({ page: 1 });
        }
      }
    },
    [pageSize, page],
  );

  return (
    <PageSizeContext.Provider value={{ ...pageSize, setSize }}>
      {children}
    </PageSizeContext.Provider>
  );
};

export const usePageSize = () => {
  const pageSize = useContext(PageSizeContext);

  // Make sure that the usePageSize hook is used within a PageSizeProvider
  if (!pageSize) {
    throw new Error("usePageSize must be used within a PageSizeProvider");
  }

  return pageSize;
};
